import { numberInputAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';
import { inputTheme } from './Input';
import { Borders, Colors, Radii, Space, Shadows, BorderWidths } from '../../types';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts.keys);

const transparentStyles = {
  border: 0,
  paddingInlineStart: 'var(--input-padding)',
  _focusVisible: {
    ring: '0',
  },
  _invalid: {
    border: 0,
    ring: 0,
  },
};

const baseStyle = definePartsStyle({
  root: {
    display: 'flex',
    flexDirection: 'row',
  },
  field: {
    border: Borders.NONE,
    boxShadow: Colors.TRANSPARENT,
    borderColor: 'none',
  },
  stepperGroup: {
    position: 'unset',
    minWidth: 'var(--input-height)',
    height: 'auto',
  },
});
const variantOutline = definePartsStyle(() => {
  return {
    root: {
      borderColor: Colors.BORDER_INPUT,
      borderWidth: BorderWidths.XS,
      borderRadius: Radii.LG,
      _hover: {
        borderColor: Colors.BORDER_EMPHASIS,
      },
      _focusWithin: {
        bg: Colors.SURFACE_PAGE,
        borderColor: Colors.BORDER_FOCUS,
        outline: '2px solid',
        outlineColor: Colors.BORDER_FOCUS,
        outlineOffset: '-1px',
        '&:has(input[aria-invalid="true"])': {
          borderColor: Colors.SURFACE_ERROR,
          outlineColor: Colors.SURFACE_ERROR,
        },
      },
      '&:has(input[aria-invalid="true"])': {
        boxShadow: 'outline-error',
        borderColor: Colors.SURFACE_ERROR,
      },
    },
    field: transparentStyles,
  };
});

const variantFlushed = definePartsStyle(() => {
  return {
    root: {
      background: Colors.TRANSPARENT,
      borderBottomColor: Colors.BORDER_DEFAULT,
      borderBottomWidth: '1px',
      _hover: {
        borderColor: Colors.BORDER_MUTED,
        borderBottomWidth: '1px',
      },
      _active: {
        borderBottomWidth: BorderWidths.SM,
        borderBottomColor: Colors.BORDER_EMPHASIS,
      },
      _focusVisible: {
        borderBottomColor: Colors.BORDER_FOCUS,
        boxShadow: Shadows.OUTLINE,
      },
    },
    field: {
      borderBottom: Borders.NONE,
    },
  };
});

const variantFilled = definePartsStyle(() => {
  return {
    root: {
      background: Colors.SURFACE_TERTIARY,
      borderColor: Colors.TRANSPARENT,
      borderWidth: '1px',
      _hover: {
        bg: Colors.SURFACE_TERTIARY_HOVER,
      },
      _focusVisible: {
        borderColor: Colors.BORDER_FOCUS,
        boxShadow: Shadows.OUTLINE,
      },
    },
    field: transparentStyles,
  };
});

const sizes = {
  xs: inputTheme.sizes?.xs.field ?? {},
  sm: inputTheme.sizes?.sm.field ?? {},
  md: inputTheme.sizes?.md.field ?? {},
  lg: inputTheme.sizes?.lg.field ?? {},
};

const variants = {
  outline: variantOutline,
  flushed: variantFlushed,
  filled: variantFilled,
};

export const NumberInput = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants,
  defaultProps: {
    size: 'md',
    variant: 'outline',
  },
});
